import { z } from 'zod';

import { dashboardT } from '../../../lib/messages';
import {
  customTargetingFormSchema,
  liverampTargetingFormSchema,
} from '../../targeting/advancedTargetingModel';
import { defaultTargetingFormValue, targetingFormSchema } from '../../targeting/model';
import {
  IntegrationCustomParameterField,
  IntegrationMediaType,
  IntegrationSchainLogicMode,
  IncompleteSchainHandling,
  IntegrationStatus,
  IntegrationType,
} from './model';
import {
  attachedRewriteListSchema,
  attachedSchainListSchema,
  createIntegrationRewriteSets,
  integrationRewriteSetSchema,
} from './integrationRewriteModel';
import type { Deal } from '../deals/model';

const requiredText = () => z.string().trim().min(1, dashboardT('api.common.validationFailed'));
const optionalNumber = z.union([z.number(), z.nan()]).optional();

export const integrationFormSchema = z
  .object({
    demandPartnerId: requiredText(),
    name: requiredText(),
    status: z.enum(IntegrationStatus),
    integrationType: z.enum(IntegrationType),
    requiredCustomParameterFields: z.array(z.enum(IntegrationCustomParameterField)),
    mediaTypes: z
      .array(z.enum(IntegrationMediaType))
      .min(1, dashboardT('integrations.validation.mediaType')),
    schainAsi: z.string(),
    sellerId: z.string(),
    notes: z.string(),
    blockBidsWithoutCat: z.boolean(),
    blockBidsWithoutAdomain: z.boolean(),
    multipleImpressionsEnabled: z.boolean(),
    multipleImpressionsCount: optionalNumber,
    duplicateDemandIntegration: z.boolean(),
    dupCount: optionalNumber,
    dupUseDefaultFloor: z.boolean(),
    dupMinFloor: optionalNumber,
    dupMaxFloor: optionalNumber,
    dupIncrement: optionalNumber,
    endpointUri: requiredText(),
    endpointUriApac: z.string(),
    endpointUriEu: z.string(),
    endpointUriUsEast: z.string(),
    endpointUriUsWest: z.string(),
    gzip: z.boolean(),
    displayManager: z.string(),
    advertiserId: z.string(),
    targetCpm: optionalNumber,
    bidFloor: optionalNumber,
    qps: optionalNumber,
    shareOfVoice: optionalNumber,
    biddingTmax: optionalNumber,
    publisherId: z.string(),
    siteId: z.string(),
    appId: z.string(),
    placementId: z.string(),
    zoneId: z.string(),
    tagId: z.string(),
    tripleliftInventoryCode: z.string(),
    openxDelDomain: z.string(),
    unrulyTargetingUuid: z.string(),
    openxUnitId: z.string(),
    smartAdServerPageId: z.string(),
    smartAdServerNetworkId: z.string(),
    smartAdServerFormatId: z.string(),
    impExt: z.string(),
    groupId: z.string(),
    bidRequestExtCo: z.string(),
    bidRequestExt: z.string(),
    integrationRewriteSets: z.array(integrationRewriteSetSchema),
    listRewrites: z.array(attachedRewriteListSchema),
    targetingId: z.string(),
    targetingName: z.string(),
    bundlesCount: z.number().int().nonnegative(),
    ipsCount: z.number().int().nonnegative(),
    pendingBundleCsv: z.custom<File>().nullable(),
    pendingIpCsv: z.custom<File>().nullable(),
    targeting: targetingFormSchema,
    customTargeting: customTargetingFormSchema,
    liverampTargeting: liverampTargetingFormSchema,
    schainLogicMode: z.enum(IntegrationSchainLogicMode),
    incompleteSchainHandling: z.enum(IncompleteSchainHandling),
    listSchains: z.array(attachedSchainListSchema),
    pmpEnabled: z.boolean(),
    privateAuction: z.boolean(),
    deals: z.array(z.custom<Deal>()),
  })
  .superRefine((value, context) => {
    const nonnegativeFields = [
      'targetCpm',
      'bidFloor',
      'qps',
      'shareOfVoice',
      'biddingTmax',
    ] as const;
    nonnegativeFields.forEach((field) => {
      const number = value[field];
      if (number !== undefined && !Number.isNaN(number) && number < 0) {
        context.addIssue({
          code: 'custom',
          path: [field],
          message: dashboardT('integrations.validation.nonnegative'),
        });
      }
    });
    if (value.qps !== undefined && !Number.isNaN(value.qps) && value.qps > 5787) {
      context.addIssue({
        code: 'custom',
        path: ['qps'],
        message: dashboardT('integrations.validation.qps'),
      });
    }
    if (
      value.shareOfVoice !== undefined &&
      !Number.isNaN(value.shareOfVoice) &&
      value.shareOfVoice > 100
    ) {
      context.addIssue({
        code: 'custom',
        path: ['shareOfVoice'],
        message: dashboardT('integrations.validation.shareOfVoice'),
      });
    }
    value.requiredCustomParameterFields.forEach((field) => {
      if (!value[field].trim()) {
        context.addIssue({
          code: 'custom',
          path: [field],
          message: dashboardT('api.common.validationFailed'),
        });
      }
    });
    if (
      value.multipleImpressionsEnabled &&
      (!Number.isInteger(value.multipleImpressionsCount) ||
        Number(value.multipleImpressionsCount) < 1 ||
        Number(value.multipleImpressionsCount) > 5)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['multipleImpressionsCount'],
        message: dashboardT('integrations.validation.impressions'),
      });
    }
    if (
      value.duplicateDemandIntegration &&
      value.dupCount !== undefined &&
      !Number.isNaN(value.dupCount) &&
      (!Number.isInteger(value.dupCount) || value.dupCount < 1)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['dupCount'],
        message: dashboardT('api.common.validationFailed'),
      });
    }
    if (
      value.dupMinFloor !== undefined &&
      value.dupMaxFloor !== undefined &&
      value.dupMinFloor > value.dupMaxFloor
    ) {
      context.addIssue({
        code: 'custom',
        path: ['dupMaxFloor'],
        message: dashboardT('integrations.validation.floorRange'),
      });
    }
  });

export type IntegrationFormValue = z.infer<typeof integrationFormSchema>;

export interface IntegrationSaveOutcome {
  targetingId?: string | undefined;
  bundlesCount?: number | undefined;
  ipsCount?: number | undefined;
  clearBundleCsv?: boolean | undefined;
  clearIpCsv?: boolean | undefined;
}

export const emptyIntegrationFormValue: IntegrationFormValue = {
  demandPartnerId: '',
  name: '',
  status: IntegrationStatus.Active,
  integrationType: IntegrationType.OpenRtb,
  requiredCustomParameterFields: [],
  mediaTypes: [],
  schainAsi: '',
  sellerId: '',
  notes: '',
  blockBidsWithoutCat: false,
  blockBidsWithoutAdomain: false,
  multipleImpressionsEnabled: false,
  multipleImpressionsCount: undefined,
  duplicateDemandIntegration: false,
  dupCount: undefined,
  dupUseDefaultFloor: false,
  dupMinFloor: undefined,
  dupMaxFloor: undefined,
  dupIncrement: undefined,
  endpointUri: '',
  endpointUriApac: '',
  endpointUriEu: '',
  endpointUriUsEast: '',
  endpointUriUsWest: '',
  gzip: false,
  displayManager: '',
  advertiserId: '',
  targetCpm: undefined,
  bidFloor: undefined,
  qps: undefined,
  shareOfVoice: undefined,
  biddingTmax: undefined,
  publisherId: '',
  siteId: '',
  appId: '',
  placementId: '',
  zoneId: '',
  tagId: '',
  tripleliftInventoryCode: '',
  openxDelDomain: '',
  unrulyTargetingUuid: '',
  openxUnitId: '',
  smartAdServerPageId: '',
  smartAdServerNetworkId: '',
  smartAdServerFormatId: '',
  impExt: '',
  groupId: '',
  bidRequestExtCo: '',
  bidRequestExt: '',
  integrationRewriteSets: createIntegrationRewriteSets(undefined),
  listRewrites: [],
  targetingId: '',
  targetingName: '',
  bundlesCount: 0,
  ipsCount: 0,
  pendingBundleCsv: null,
  pendingIpCsv: null,
  targeting: structuredClone(defaultTargetingFormValue),
  customTargeting: { groups: [] },
  liverampTargeting: { groups: [] },
  schainLogicMode: IntegrationSchainLogicMode.ResellerPriority,
  incompleteSchainHandling: IncompleteSchainHandling.DropRequest,
  listSchains: [],
  pmpEnabled: false,
  privateAuction: false,
  deals: [],
};
